import { t } from "@hakili/i18n";
import { Camera, Car } from "lucide-react";
import { useState } from "react";

/**
 * `round CW`, `CW.3`: **the gallery, under `6.8.1`.**
 *
 * The founder's law of 7 September, already kept by the dealer's space and
 * booked beside `6.8`: **a big image exists to be seen WHOLE; a crop is for a
 * thumbnail.** So the large photograph is `object-contain`, centred, never cut,
 * and the frame behind it is filled by an enlarged, blurred, darkened copy of
 * the SAME image. The thumbnail strip and the card keep `object-cover`, because
 * a thumbnail is a marker and not a picture.
 *
 * **The count is the length of what came back**, never a constant: the August
 * drawing wrote « 8 photos » in hard text, and this reads `i + 1 sur n` off the
 * list it is showing. `R81`, and the drawing's own correction of itself.
 */

export interface Photo {
  readonly id: string;
  /** The named view, in the dealer's own vocabulary (`R76`). */
  readonly view: string | null;
  readonly url: string;
}

export interface GalleryProps {
  readonly photos: readonly Photo[];
  /** Rendered over the image , the back arrow on the fiche. */
  readonly overImage?: React.ReactNode;
  readonly height?: string;
}

export function Gallery({ photos, overImage, height = "h-52" }: GalleryProps): React.JSX.Element {
  const [at, setAt] = useState(0);
  const index = photos.length === 0 ? -1 : Math.min(at, photos.length - 1);
  const active = index < 0 ? null : photos[index];

  if (active === undefined || active === null) {
    return (
      <div
        data-testid="galerie-vide"
        className={`${height} flex flex-col items-center justify-center gap-2 bg-gray-100`}
      >
        <Car className="h-8 w-8 text-gray-300" aria-hidden="true" />
        <p className="text-xs text-gray-400">{t("client.vehicule.sansPhoto")}</p>
      </div>
    );
  }

  return (
    <div data-testid="galerie">
      <div className={`relative overflow-hidden bg-gray-900 ${height}`}>
        {/* The frame, filled BEHIND: the same image, enlarged and blurred, so a
            photograph of any proportion sits on something rather than in a band
            of grey. `aria-hidden`, because it is the same picture twice. */}
        <img
          src={active.url}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-110 object-cover blur-xl brightness-[0.55]"
        />
        {/* The image, ENTIRE. */}
        <img
          src={active.url}
          alt={active.view ?? t("client.vehicule.photo")}
          data-testid="galerie-grande"
          className="relative z-10 mx-auto max-h-full max-w-full object-contain"
        />
        {active.view === null ? null : (
          <span className="absolute bottom-3 left-3 z-20 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-700">
            {active.view}
          </span>
        )}
        <span className="absolute bottom-3 right-3 z-20 flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1">
          <Camera className="h-3.5 w-3.5 text-gray-400" aria-hidden="true" />
          <span className="text-xs tabular-nums text-gray-500" data-testid="galerie-compte">
            {index + 1} {t("client.vehicule.sur")} {photos.length}
          </span>
        </span>
        {overImage}
      </div>

      {photos.length > 1 ? (
        <div className="flex items-start gap-2 overflow-x-auto px-4 pb-1 pt-3">
          {photos.map((photo, position) => (
            <button
              key={photo.id}
              type="button"
              data-testid={`galerie-vignette-${String(position)}`}
              aria-current={position === index ? "true" : undefined}
              onClick={() => {
                setAt(position);
              }}
              className="w-16 shrink-0 text-left"
            >
              {/* `object-cover` here, and that is the law's other half: a
                  thumbnail is a marker, and a row of letterboxed strips is
                  unreadable at this size. */}
              <img
                src={photo.url}
                alt={photo.view ?? ""}
                className={`h-12 w-16 rounded-lg object-cover ${
                  position === index ? "ring-2 ring-indigo-500" : "opacity-80"
                }`}
              />
              <span
                className={`mt-1 block truncate text-[10px] ${
                  position === index ? "font-medium text-indigo-600" : "text-gray-400"
                }`}
              >
                {photo.view ?? ""}
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
